import { FunctionComponent, useEffect, useState } from 'react';
import IcelandairLogo from '../../assets/icelandairLogo.svg';
import { Button, IconButton } from '../../tailwindcomponents';
import { Close } from '../../components/icons';
import { DatePicker } from '../../MaterialUiComponents';
import { Autocomplete, TextField, Typography, Input } from '../../tailwindcomponents';
import { destinations, WorldTrip } from '../../types/worldTools';
import worldTools from '../../api/worldTools';
import { EmployeeSearch } from '../../components';
import { User } from '../../types/user';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { ToolData } from '../../types/tool';
import { Collection } from '../../components/Collection';
import taskApi from './../../api/tasks';
import WorldToolPdf from './WorldToolPdf';
import ToolsList from './ToolsList';

type NewWorldTripProps = {
    closeForm: Function,
    selected?: WorldTrip,
    allDestinations: destinations[],
}

// This page contains the form for both editing and creating World Trips (based on viewType value)
const NewWorldTrip: FunctionComponent<NewWorldTripProps> = ({ closeForm, selected, allDestinations }) => {
    const store = useSelector((state: RootState) => state);
    const isWorldToolAdmin = store.user.me.user_clearence_groups.includes("World Tools Edit");
    const [selectedTrip, setSelectedTrip] = useState<WorldTrip>();
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [destination, setDestination] = useState<destinations | undefined>(selected && allDestinations.find(dest => dest.location === selected.destination));
    const [name, setName] = useState<string>("");
    const [reason, setReason] = useState<string>("");
    const [rfid, setRfid] = useState<string>("");
    const [employees, setEmployees] = useState<User[]>([]);
    const [tools, setTools] = useState<ToolData[]>([]);
    const [employeeIds, setEmployeeIds] = useState<number[]>([]);
    const [toolIds, setToolIds] = useState<number[]>([]);
    const [tempTail, setTempTail] = useState<string | null>("");
    const [errorMessage, setErrorMessage] = useState("");
    const [tails, setTails] = useState<string[]>([]);

    useEffect(() => {
        if (selected !== undefined) {
            getWorldTripById(selected.id);
        }
    }, [selected]);

    useEffect(() => {
        taskApi.GetAllTailNumbers().then(res => {
            setTails(res.data);
        })
    }, []);

    // Populating all values based on the trip props when that's included in props (Triggered in use effect) Collection component created and used here to display selected items of any sort
    const getWorldTripById = (worldTripId: number) => {
        worldTools.GetWorldTripById(worldTripId).then(res => {
            setSelectedTrip(res.data);
            setName(res.data.general_info.name);
            setStartDate(new Date(res.data.general_info.start_date));
            setEndDate(new Date(res.data.general_info.end_date));
            setReason(res.data.general_info.reason);
            setEmployees(res.data.assigned_employees);
            setTools(res.data.loaned_out_tools);
            setTempTail(res.data.general_info.tail_number);
            const tempDestination = allDestinations.find(dest => dest.id === res.data.general_info.location_id);
            setDestination(tempDestination);
        });
    }
    const onSelectedEmployeeRow = (employee: User | null) => {
        if (employee !== null) {
            setEmployees([...employees, employee]);
            setEmployeeIds([...employeeIds, employee.user_id]);
        }
    }
    // This is fired on keydown event in input field for tools. Logic applies if the key clicked was enter (Scanner types in the code and automatically hit's enter)
    const onSelectedTool = (keyCode: any) => {
        if (keyCode === 13) {
            let tempTool = store.tools.mxi_tools.find(s => s.rf_id === rfid);
            if (tempTool !== undefined) {
                setTools([...tools, tempTool]);
                setToolIds([...toolIds, tempTool.mxi_inv_no_id]);
                setRfid("");
            }
        }
    }
    const saveWorldTrip = () => {
        if (destination) {
            if (selected && selectedTrip) {
                if (selected.status !== "Complete") {
                    selectedTrip.assigned_employees = employees;
                    selectedTrip.loaned_out_tools = tools;
                    selectedTrip.general_info.last_modified_by_employee_id = store.user.me.user_id;
                    selectedTrip.general_info.tail_number = tempTail;
                    selectedTrip.general_info.start_date = startDate;
                    selectedTrip.general_info.end_date = endDate;
                    selectedTrip.general_info.location_id = destination.id;
                    selectedTrip.general_info.name = name;
                    selectedTrip.general_info.reason = reason;
                    worldTools.UpdateWorldTrip(selectedTrip).then(res => {
                        if (res.status === false) {
                            setErrorMessage(res.message);
                        } else {
                            closeForm();
                        }
                    });
                }
            } else {
                worldTools.CreateNewWorldTrip(destination.id, reason, startDate, endDate, employees, tools, name, tempTail).then(res => {
                    if (res.status === false) {
                        setErrorMessage(res.message);
                    } else {
                        closeForm();
                    }
                });
            }
        }
    }
    const removeEmployee = (employeeId: number) => {
        const employeeToRemove = employees.findIndex(employee => employee.user_id === employeeId);
        const tempEmployees = [...employees];
        tempEmployees.splice(employeeToRemove, 1);
        setEmployees(tempEmployees);
    }
    const OnDateChange = (status: 'Start' | 'End', date: Date) => {
        if (status === 'Start') {
            setStartDate(date);
        } else {
            if (new Date(startDate) > new Date(date)) {
                setErrorMessage("End date is incorrect");
            } else {
                setErrorMessage("");
            }
            setEndDate(date);
        }
    }
    return (
        <div className="mb-4">
            <div className="flex flex-wrap justify-center w-full">
                <div className="w-7/12">
                    <Typography color='primary' variant='h1' text={selected ? selected.trip_name : "Create new World Trip"} align='left' />
                </div>
                <div className="w-1/3">
                    <img src={IcelandairLogo} className="h-[50px] bottom-[5px] relative" alt="Logo" />
                </div>
                <div className="w-1/12">
                    <IconButton onClick={() => closeForm()} color="default" >
                        <Close />
                    </IconButton>
                </div>
            </div>
            <hr />
            <div className="flex flex-wrap w-full">
                <div className="w-full lg:w-7/12">
                    {selectedTrip && <WorldToolPdf trip={selectedTrip!} />}
                    <div className="grid grid-cols-2 w-full gap-4 mt-10">
                        <div>
                            <Input multiline={true} help_text={"Set a name for the world trip"} label_text={"World Trip Name"} value={name} onTextChange={(text: string) => setName(text)} type="text" width={100} />
                        </div>
                        <div>
                            <Autocomplete
                                fullWidth
                                noOptionsText="No tails found"
                                options={allDestinations}
                                getOptionLabel={option => option.location}
                                defaultValue={destination}
                                value={destination}
                                clearOnEscape
                                autoHighlight
                                onChange={(_e: any, newValue: destinations | string | null) => newValue && typeof newValue !== 'string' ? setDestination(newValue) : {}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Destination" variant="standard" />
                                )} />
                        </div>
                        <div className="w-1/3">
                            <DatePicker type="Date" selected={startDate} label="Start date" onDateChange={(date: Date) => OnDateChange("Start", date)} />
                        </div>
                        <div className="w-1/3">
                            <DatePicker type="Date" selected={endDate} label="End date" onDateChange={(date: Date) => OnDateChange("End", date)} errorString={errorMessage} />
                        </div>
                        <div className="w-1/3">
                            <Autocomplete
                                fullWidth
                                options={tails}
                                value={tempTail}
                                noOptionsText="No tails found"
                                clearOnEscape
                                autoHighlight
                                onChange={(e: any, newValue: string | null) => setTempTail(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Tails" margin="none" variant="standard" size="small" fullWidth />
                                )} />
                        </div>
                        <div className="w-full">
                            <Input multiline={true} help_text={"What is the Reason for this world trip. Write minimum of 50 words"} label_text={"Reason for World Trip"} value={reason} onTextChange={(text: string) => setReason(text)} type="text" width={100} />
                        </div>
                        <div className="w-1/2">
                            {isWorldToolAdmin && selected?.status !== "Complete" &&
                                <EmployeeSearch clearOnSelect={true} fullUser={true} place_holder={"All employees"} onSelectedEmployee={(employee: User | null) => onSelectedEmployeeRow(employee)} />
                            }
                        </div>
                        <div className="w-1/2">
                            {isWorldToolAdmin && selected?.status !== "Complete" &&
                                <Input multiline={false} help_text={"Please scan your tool for this trip"} label_text={"Scan Tool"} value={rfid} onKeyDown={(keyDown: any) => onSelectedTool(keyDown.keyCode)} onTextChange={(text: string) => setRfid(text.toUpperCase())} type="text" width={100} />
                            }
                        </div>
                        <div className="w-full">
                            {
                                isWorldToolAdmin && selected?.status !== "Complete" &&
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    onClick={() => { saveWorldTrip() }}>
                                    {selected ? "Save Changes" : "Create new World Tool"}
                                </Button>}
                        </div>
                    </div>
                </div>
                <div className="w-full lg:w-5/12">
                    <Collection edit={isWorldToolAdmin && selected?.status !== "Complete"} collectionList={employees} title="Employees assigned to trip" idProperty="user_id" removeItem={(itemId: number) => removeEmployee(itemId)} />
                    <ToolsList tools={tools} />
                </div>
            </div>
        </div >
    );
};
export default NewWorldTrip;
